import React, { useRef, useState, useEffect } from 'react'
import { ReactTinyLink } from 'react-tiny-link'
import clsx from 'clsx'
import { FuseScrollbars, FuseAnimateGroup } from '@fuse'
import * as R from 'ramda'
import {
  Avatar,
  Badge,
  Paper,
  Typography,
  TextField,
  IconButton,
  Icon,
  CircularProgress,
  Link,
} from '@material-ui/core'
import Assets from 'app/configs/assetConfigs'
import { makeStyles, withStyles } from '@material-ui/styles'
import { usePlace } from 'commons/usePlace'
import { useUserProfile } from 'commons/useUserProfile'
import { MessageStatus } from 'app/main/messages/conversations/useConversationSocket'
import PersonAvatar from 'app/components/PersonAvatar'
import { getConversationDateTime } from 'commons/date'
import Loader from 'app/components/Loader'
import ErrorFetchingLoading from 'app/main/messages/notificationTimeline/ErrorFetchingLoading'
import { FormattedMessage, useIntl } from 'react-intl'
import ErrorBoundary from 'app/components/ErrorBoundary'
import { ReactComponent as ErrorIcon } from 'app/components/icons/conv-fail-icon.svg'
import { ReactComponent as SentIcon } from 'app/components/icons/conv-sent-icon.svg'
import { ReactComponent as ReadIcon } from 'app/components/icons/conv-read-icon.svg'
import { ConversationStatus } from 'app/main/messages/conversations/useConversation'
import { getAvatarPlaceHolder, isStartWithEng } from 'libs/text'
import { useDefaultLanguage } from 'commons/useDefaultLanguage'
import { useFlag } from '@unleash/proxy-client-react'

const ADMIN_STATUS = {
  INVITED: 2,
  SUSPEND: 3,
}

const MAX_INPUT_ROW = 5
const KEYBOARD_ENTER_CODE = 13

const useStyles = makeStyles((theme) => ({
  messageRow: {
    '&.bubble-contact': {
      '& .bubble': {
        backgroundColor: theme.palette.chat.userBubbleColor,
        color: theme.palette.chat.userTextColor,
        borderTopLeftRadius: 5,
        borderBottomLeftRadius: 5,
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        '& .time': {
          marginLeft: 8,
        },
        '& a': {
          color: theme.palette.chat.linkTextColor,
        },
      },
      '&.first-of-group': {
        '& .bubble': {
          borderTopLeftRadius: 5,
        },
      },
      '&.last-of-group': {
        '& .bubble': {
          borderBottomLeftRadius: 5,
        },
      },
    },
    '&.bubble-me': {
      paddingLeft: 40,
      '& .avatar': {
        order: 2,
        margin: '0 0 0 16px',
      },
      '& .time': {
        justifyContent: 'flex-end',
        right: 0,
        marginRight: 12,
      },
      '& .tinyLink': {
        color: theme.palette.chat.linkTextColor,
        '& a': {
          color: theme.palette.chat.linkTextColor,
        },
      },
      '& .bubble': {
        marginLeft: 'auto',
        backgroundColor: theme.palette.chat.selfBubbleColor,
        color: theme.palette.chat.selfTextColor,
        borderTopLeftRadius: 5,
        borderBottomLeftRadius: 5,
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        '& .time': {
          justifyContent: 'flex-end',
          right: 0,
          marginRight: 12,
        },
        '& a': {
          color: theme.palette.chat.linkTextColor,
        },
      },
      '&.first-of-group': {
        '& .bubble': {
          borderTopRightRadius: 5,
        },
      },

      '&.last-of-group': {
        '& .bubble': {
          borderBottomRightRadius: 5,
        },
      },
    },
    '&.bubble-admin': {
      '& .bubble': {
        marginRight: '2.6rem',
        marginLeft: 'auto',
        backgroundColor: theme.palette.chat.adminBubbleColor,
        color: theme.palette.chat.adminTextColor,
        borderTopLeftRadius: 5,
        borderBottomLeftRadius: 5,
        borderTopRightRadius: 5,
        borderBottomRightRadius: 5,
        '& a': {
          color: theme.palette.chat.linkTextColor,
        },
      },
      '& .avatar': {
        right: 0,
        order: 2,
        margin: '0 0 0 16px',
        borderWidth: 2,
        borderColor: '#187fb7',
        borderStyle: 'solid',
      },
      '& .avatar-suspend': {
        right: 0,
        order: 2,
        margin: '0 0 0 16px',
        borderWidth: 2,
        borderColor: '#707070',
        borderStyle: 'solid',
      },
      '& .time': {
        justifyContent: 'flex-end',
        right: 0,
        marginRight: 12,
        textAlign: 'right',
      },
      '& .badge': {
        right: 0,
        marginRight: '1.6rem !important',
      },
      '&.first-of-group': {
        '& .bubble': {
          borderTopLeftRadius: 5,
        },
      },
      '&.last-of-group': {
        '& .bubble': {
          borderBottomLeftRadius: 5,
        },
      },
      '& .reactTinyLink': {
        marginRight: '5.0rem',
      },
    },
    '&.bubble-contact + .bubble-me, &.bubble-me + .bubble-contact': {
      paddingTop: 20,
      marginTop: 20,
    },
    '&.first-of-group': {
      '& .bubble': {
        borderTopLeftRadius: 5,
        paddingTop: 13,
      },
    },
    '&.last-of-group': {
      '& .bubble': {
        borderBottomLeftRadius: 5,
        paddingBottom: 13,
        '& .time': {
          right: 0,
          display: 'flex',
        },
      },
    },
  },
  chatForm: {
    '&.chat-form': {
      backgroundColor: '#f2f2f2',
    },
  },
  wordBreak: {
    wordBreak: 'break-word',
  },
}))

const SmallAvatar = withStyles((theme) => ({
  root: {
    width: 22,
    height: 22,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}))(Avatar)

const ErrorView = (props) => <div></div>

const MsgFailIcon = () => {
  return (
    <span className="flex justify-end ltr:ml-2 rtl:mr-2">
      <ErrorIcon className="w-26 h-26 mt-auto mb-2" />
    </span>
  )
}

const ConversationRoom = (props) => {
  const {
    conversationSelected,
    chatRoomData,
    handleSendMessage,
    resendMessage,
    deleteMessage,
    translateMessage,
    isLoading,
    fetchErrorChat,
    refetchConversationRoom,
    lastKey,
    dispatchConversationSocket,
    showOriginalMessage,
  } = props
  const [currentChatId, setCurrentChatId] = useState('')
  const [preventScroll, setPreventScroll] = useState(false)
  const defaultLanguge = useDefaultLanguage()
  const place = usePlace()
  const userProfile = useUserProfile()
  const { formatMessage } = useIntl()

  useEffect(() => {
    //preload message
    let img = new Image()
    img.src = Assets.conversation.msgFailIcon
  }, [])

  useEffect(() => {
    setCurrentChatId(conversationSelected.historyId)
  }, [conversationSelected])

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (
      chatRoomData &&
      lastKey !== '' &&
      chatRoomData.data.length > 0 &&
      !preventScroll
    ) {
      scrollToBottom()
    }
  }, [chatRoomData])
  /* eslint-disable react-hooks/exhaustive-deps */

  const mes_4386_dashbooard_chats_coming_soon = useFlag(
    'mes_4386_dashbooard_chats_coming_soon',
  )

  function scrollToBottom() {
    if (!R.isNil(chatRef.current))
      chatRef.current.scrollTop = chatRef.current.scrollHeight
    setPreventScroll(false)
  }

  function shouldShowContactAvatar(item, i) {
    return (
      (item.role === 'user' ||
        (item.role === 'admin' && !isSelfFromItem(item))) &&
      ((chatRoomData.data[i + 1] &&
        senderId(chatRoomData.data[i + 1]) !==
          senderId(chatRoomData.data[i])) ||
        !chatRoomData.data[i + 1])
    )
  }

  function isOtherAdmin(item) {
    return item.role === 'admin' && !isSelfFromItem(item)
  }

  function isFirstMessageOfGroup(item, i) {
    return (
      i === 0 ||
      (chatRoomData.data[i - 1] &&
        senderId(chatRoomData.data[i - 1]) !== senderId(item))
    )
  }

  function renderMessage(item) {
    if (item.role === 'admin') {
      return item.message
    }
    return item.showOriginalMessage ? item.message : item.translatedMessage
  }

  function isLastMessageOfGroup(item, i) {
    return (
      i === chatRoomData.data.length - 1 ||
      (chatRoomData.data[i + 1] &&
        senderId(chatRoomData.data[i + 1]) !== senderId(item))
    )
  }

  function isMessageSent(item) {
    if (!R.isNil(item.status)) {
      return item.status !== MessageStatus.SENDING
    }
    return true
  }

  function isAllowedTranslation(item) {
    return item.role === 'user' && (item.allowTranslation || false)
  }

  function isAdmin(item) {
    return item.role === 'admin'
  }

  function isTranslating(item) {
    return item.isTranslating || false
  }

  function isTranslatingFailed(item) {
    return item.isTranslatingFailed || false
  }

  function showTranslateButton(item) {
    return item.translatedMessage == null
  }

  function showToggleTranslateButton(item) {
    return item.showOriginalMessage && item.translatedMessage != null
  }

  function showToggleOriginalButton(item) {
    return !item.showOriginalMessage
  }

  function isMessageFail(item) {
    return item.is_fail
  }

  function onInputChange(ev) {
    setMessageText(ev.target.value)
  }

  function performSendMessage() {
    if (messageText === '') {
      return
    }

    handleSendMessage(
      currentChatId,
      place.id,
      conversationSelected.subscriberId,
      messageText,
      scrollToBottom,
    )
    scrollToBottom()
    setMessageText('')
  }

  function handleKeyDown(ev) {
    if (ev.which === KEYBOARD_ENTER_CODE) {
      ev.preventDefault()
      performSendMessage()
    }
  }

  function onMessageSubmit(ev) {
    ev.preventDefault()
    performSendMessage()
  }

  const classes = useStyles(props)
  const chatRef = useRef(null)
  const [messageText, setMessageText] = useState('')

  const [isReachTop, setIsReachTop] = useState(false)
  const [firstItem, setFirstItem] = useState(undefined)

  const deletedPlaceholderText = formatMessage({
    id: 'conversation.subscriberDeleted.placeholder',
  })

  useEffect(() => {
    if (isReachTop) {
      dispatchConversationSocket({ type: 'SET_LOADING_MORE' })
    }
  }, [isReachTop, setIsReachTop])

  useEffect(() => {
    if (firstItem && isReachTop) {
      const firstItemElement = document.querySelector(`#chat-${firstItem}`)
      if (firstItemElement) {
        firstItemElement.scrollIntoView()
      }
      setIsReachTop(false)
    }
    setFirstItem(R.path(['data', 0, 'uuid'], chatRoomData))
  }, [R.length(chatRoomData.data)])

  if (fetchErrorChat) {
    if (fetchErrorChat === 'error') {
      return (
        <div className={clsx('flex flex-col relative', props.className)}>
          <div className="mt-128">
            <ErrorFetchingLoading
              title={formatMessage({ id: 'error.network.unavailable.title' })}
              isShow={true}
              onLoadSuccess={refetchConversationRoom}
              subtitle={formatMessage({
                id: 'error.network.unavailable.body',
              })}
            />
          </div>
        </div>
      )
    }
    return (
      <div className={clsx('flex flex-col relative', props.className)}>
        <div className="mt-128">
          <ErrorFetchingLoading
            title={formatMessage({ id: 'error.internet.server.error' })}
            isShow={true}
            onLoadSuccess={refetchConversationRoom}
            subtitle={formatMessage({ id: 'conversation.error.try.again' })}
          />
        </div>
      </div>
    )
  }

  // eslint-disable-next-line no-useless-escape
  const URL_REGEX =
    /(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?|^((http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3,4}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/g

  const getFirstLink = (message) => {
    message = message.replace(/\n/g, ' ')
    let len = R.lensIndex(0)
    return R.compose(
      R.unless(R.isNil, (data) => {
        try {
          if (!data.startsWith('http')) {
            return new URL(R.concat('https://', data)).toString()
          } else {
            return new URL(data).toString()
          }
        } catch (e) {
          return null
        }
      }),
      R.view(len),
      R.match(URL_REGEX),
    )(message)
  }

  const userFirstName = (sender, deletedPlaceholderText) => {
    return R.ifElse(
      R.isNil,
      R.always(deletedPlaceholderText),
      R.prop('first_name'),
    )(sender)
  }

  const senderId = R.path(['sender', 'id'])

  const isSelf = (id) => {
    return R.equals(id, userProfile.id)
  }
  const isSelfFromItem = R.compose(isSelf, senderId)

  const renderRemarkFoorter = (message) => (
    <div className="inline-block text-center justify-center bottom-0 ltr:py-8 ltr:px-8 rtl:py-8 rtl:px-8 flex pb-32 text-gray-500">
      {message}
    </div>
  )

  function conversationFooter() {
    if (mes_4386_dashbooard_chats_coming_soon) {
      return renderRemarkFoorter(
        <div className="text-14 text-blue-950 p-14 rounded-8 flex flex-col gap-12">
          <div>
            <FormattedMessage id="conversations.new.comingSoon.title" />
          </div>
          <div>
            <FormattedMessage id="conversations.new.comingSoon.subtitle" />
          </div>
        </div>,
      )
    } else if (
      R.propEq(
        'conversationStatus',
        ConversationStatus.SUBSCRIBER_DELETED,
        conversationSelected,
      )
    ) {
      return renderRemarkFoorter(
        formatMessage({
          id: 'conversation.subscriberDeleted.description',
        }),
      )
    } else if (
      R.propEq(
        'conversationStatus',
        ConversationStatus.SUBSCRIBER_UNSUBSCRIBED,
        conversationSelected,
      ) ||
      R.isNil(conversationSelected.historyId)
    ) {
      return renderRemarkFoorter(
        formatMessage(
          {
            id: 'conversation.user.unsubscribed.remark',
          },
          {
            newLine: <br key={'remark'} />,
          },
        ),
      )
    } else if (conversationSelected) {
      return (
        <form
          onSubmit={onMessageSubmit}
          className={clsx(
            classes.chatForm,
            'absolute bottom-0 left-0 right-0 p-8 chat-form flex z-40',
          )}
          autoComplete="off"
        >
          <Paper
            className="flex items-center relative rounded-24 w-full"
            variant="outlined"
            elevation={0}
          >
            <TextField
              autoFocus={false}
              multiline={true}
              rowsMax={MAX_INPUT_ROW}
              id="message-input"
              className="flex-1"
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: 'flex flex-grow flex-shrink-0 ltr:ml-16 rtl:mr-16 ltr:mr-48 rtl:ml-48 my-8',
                  input: '',
                },
                placeholder: formatMessage({
                  id: 'conversationRoom.placeholder.in.input.message',
                }),
              }}
              inputProps={{
                maxLength: 500,
              }}
              InputLabelProps={{
                shrink: false,
                className: classes.bootstrapFormLabel,
              }}
              onChange={onInputChange}
              onKeyDown={handleKeyDown}
              value={messageText}
            />
          </Paper>
          <IconButton type="submit">
            <Icon className="text-24" color="action">
              send
            </Icon>
          </IconButton>
        </form>
      )
    } else {
      return null
    }
  }

  return (
    <div className={clsx('flex flex-col relative', props.className)}>
      <Loader
        timeout={0}
        isLoading={isLoading}
        placeholder={
          <FuseAnimateGroup
            enter={{ animation: 'fadeIn' }}
            leave={{ animation: 'fadeOut' }}
          >
            <div className="w-full h-full mt-24 ltr:mr-8 rtl:ml-8 text-center">
              <CircularProgress color="secondary" size={35} />
            </div>
          </FuseAnimateGroup>
        }
      >
        <FuseScrollbars
          ref={chatRef}
          className="flex flex-1 flex-col static overflow-x-hidden"
          onYReachStart={() => {
            setIsReachTop(true)
          }}
          onScrollDown={() => {
            setIsReachTop(false)
          }}
          onScrollUp={() => {
            setPreventScroll(true)
          }}
          onYReachEnd={() => {
            setPreventScroll(false)
          }}
        >
          <div className="flex flex-col pt-16 ltr:pl-56 rtl:pr-56 ltr:pr-16 rtl:pl-16 pb-40">
            {chatRoomData.data.map((item, i) => {
              let link = getFirstLink(item.message)
              return (
                <div
                  id={`chat-${item.uuid}`}
                  key={item.uuid}
                  className={clsx(
                    classes.messageRow,
                    'flex flex-col flex-grow-0 flex-shrink-0 items-start justify-end relative pr-16 pl-16 pb-4',
                    { 'bubble-me': isSelfFromItem(item) },
                    {
                      'bubble-contact': item.role === 'user',
                    },
                    {
                      'bubble-admin':
                        !isSelfFromItem(item) && item.role === 'admin',
                    },
                    { 'first-of-group': isFirstMessageOfGroup(item, i) },
                    { 'last-of-group': isLastMessageOfGroup(item, i) },
                    i + 1 === chatRoomData.data.length && 'pb-96',
                  )}
                >
                  {shouldShowContactAvatar(item, i) && !isOtherAdmin(item) && (
                    <PersonAvatar
                      className="avatar absolute m-0 ltr:-ml-48 rtl:-mr-48"
                      placeHolder={
                        isStartWithEng(
                          R.path(['sender', 'first_name'])(item),
                        ) &&
                        isStartWithEng(R.path(['sender', 'last_name'])(item))
                          ? getAvatarPlaceHolder(
                              R.path(['sender', 'first_name'])(item),
                              R.path(['sender', 'last_name'])(item),
                            )
                          : null
                      }
                      subId={R.path(['sender', 'place_subscriber_id'])(item)}
                      src={R.path(['sender', 'avatar'], item)}
                    />
                  )}

                  <div className="bubble flex flex-row bg-transparent p-0 max-w-9/10">
                    {isSelfFromItem(item) && isMessageSent(item) && (
                      <span className="flex justify-end ltr:mr-2 rtl:ml-2">
                        {item.is_read ? (
                          <ReadIcon className="mt-auto mb-2 ltr:mr-2 rtl:ml-2" />
                        ) : (
                          <SentIcon className="mt-auto mb-2 ltr:mr-2 rtl:ml-2" />
                        )}
                      </span>
                    )}

                    <div
                      className={clsx(
                        classes.wordBreak,
                        'bubble flex relative items-center justify-center p-12',
                      )}
                    >
                      <div
                        className={clsx(
                          'leading-tight whitespace-pre-wrap',
                          { 'break-all': link },
                          { 'break-words': !link },
                        )}
                      >
                        {renderMessage(item)}
                      </div>
                    </div>
                    {isMessageFail(item) && <MsgFailIcon />}
                  </div>

                  {link && (
                    <div
                      className={clsx(
                        'bubble flex flex-end mt-4 relative break-all pb-0 pt-0 w-1/2 bg-transparent reactTinyLink',
                        {
                          'justify-end': isSelfFromItem(item),
                        },
                        {
                          'justify-start': !isSelfFromItem(item),
                        },
                      )}
                    >
                      <ErrorBoundary fallback={<ErrorView />}>
                        <ReactTinyLink
                          className={clsx('w-auto')}
                          width="100%"
                          cardSize="large"
                          showGraphic={true}
                          maxLine={2}
                          minLine={1}
                          defaultMedia={Assets.conversation.previewDefault}
                          url={link}
                        />
                      </ErrorBoundary>
                    </div>
                  )}
                  {shouldShowContactAvatar(item, i) && isOtherAdmin(item) && (
                    <Badge
                      className={'absolute badge m-0 z-0 mb-24'}
                      overlap="circle"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                      }}
                      badgeContent={
                        item.sender &&
                        item.sender.status === ADMIN_STATUS.SUSPEND ? (
                          <SmallAvatar
                            alt="icon"
                            src="/assets/images/icons/notifyme-icon-grey.png"
                          />
                        ) : (
                          <SmallAvatar alt="icon" src="/favicon.ico" />
                        )
                      }
                    >
                      {item.sender ? (
                        <PersonAvatar
                          className={clsx('avatar m-0 ltr:-ml-32 rtl:-mr-32 ', {
                            'avatar-suspend':
                              item.sender.status === ADMIN_STATUS.SUSPEND,
                          })}
                          placeHolder={
                            isStartWithEng(
                              R.path(['sender', 'first_name'])(item),
                            ) &&
                            isStartWithEng(
                              R.path(['sender', 'last_name'])(item),
                            )
                              ? getAvatarPlaceHolder(
                                  R.path(['sender', 'first_name'])(item),
                                  R.path(['sender', 'last_name'])(item),
                                )
                              : null
                          }
                          src={item.sender.avatar}
                        />
                      ) : (
                        <SmallAvatar alt="icon" src="/favicon.ico" />
                      )}
                    </Badge>
                  )}

                  {isMessageSent(item) && (
                    <div
                      className={clsx(
                        'flex flex-row items-center space-x-4 mt-8 -mr-24 w-full',
                        {
                          'justify-end': isAdmin(item),
                        },
                      )}
                    >
                      <div className={clsx('flex flex-row')}>
                        {' '}
                        <Typography
                          className="time text-11 w-full"
                          color="textSecondary"
                        >
                          {isSelfFromItem(item)
                            ? formatMessage({ id: 'text.you' })
                            : userFirstName(
                                R.prop('sender', item),
                                deletedPlaceholderText,
                              )}{' '}
                          {' • '}
                          {getConversationDateTime(item.created)}
                        </Typography>
                      </div>

                      {isAllowedTranslation(item) && (
                        <div className="flex flex-row items-center space-x-4 ml-4">
                          {
                            // MARK:- Button for calling translation socket event
                            showTranslateButton(item) && (
                              <Link
                                className="cursor-pointer text-12 font-bold"
                                color="secondary"
                                onClick={() => {
                                  translateMessage(
                                    item.uuid,
                                    item.languageCode,
                                    defaultLanguge.code,
                                    item.message,
                                  )
                                }}
                              >
                                {formatMessage({
                                  id: 'conversationRoom.translation.label',
                                })}
                              </Link>
                            )
                          }

                          {showToggleTranslateButton(item) && (
                            <Link
                              className="cursor-pointer text-12 font-bold"
                              color="secondary"
                              onClick={() => {
                                showOriginalMessage(item.uuid, false)
                              }}
                            >
                              {formatMessage({
                                id: 'conversationRoom.translation.seeTranslation.label',
                              })}
                            </Link>
                          )}

                          {showToggleOriginalButton(item) && (
                            <Link
                              className="cursor-pointer text-12 font-bold"
                              color="secondary"
                              onClick={() => {
                                showOriginalMessage(item.uuid, true)
                              }}
                            >
                              {formatMessage({
                                id: 'conversationRoom.translation.seeOriginal.label',
                              })}
                            </Link>
                          )}

                          {isTranslating(item) && (
                            <CircularProgress
                              size={15}
                              color="secondary"
                              className={classes.progress}
                            />
                          )}

                          {isTranslatingFailed(item) && (
                            <div className="flex flex-row space-x-4 items-center">
                              <Typography className="text-11 text-gray-500">
                                |
                              </Typography>
                              <p className="text-11" style={{ color: 'red' }}>
                                {formatMessage({
                                  id: 'conversationRoom.translation.failure.label',
                                })}
                              </p>
                              <Icon
                                className={clsx('text-24')}
                                style={{ color: 'red' }}
                              >
                                reportProblem
                              </Icon>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {isMessageFail(item) && (
                    <div className="bubble ltr:pl-2 rtl:pr-2 pb-28 bg-transparent">
                      <div className="time flex absolute w-full text-13 text-gray-600 mt-8 -mb-24 whitespace-nowrap">
                        <Link
                          className="cursor-pointer text-gray-600 ltr:pr-1 rtl:pl-1"
                          onClick={() => {
                            resendMessage(item.tempUuid)
                          }}
                        >
                          {formatMessage({ id: 'conversation.message.resend' })}
                        </Link>
                        {' | '}
                        <Link
                          className="cursor-pointer text-gray-600 ltr:pl-1 rtl:pr-1"
                          onClick={() => {
                            deleteMessage(item.tempUuid)
                          }}
                        >
                          {formatMessage({ id: 'conversation.message.delete' })}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </FuseScrollbars>

        {conversationFooter()}
      </Loader>
    </div>
  )
}

export default ConversationRoom
